import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import Modal from "../../components/shared/Modal/Modal";
import { useTranslation } from "react-i18next";
import { ServiceNextProject } from "../../services/ServiceNextProject";
import { Loading } from "../../components/shared";
import { NotificationManager } from "react-notifications";
import styles from './ImportNextProjectModal.module.scss';
import TooltipContainer from "../../components/shared/TooltipContainer/TooltipContainer";
import StyledCheckbox from "../../components/shared/StyledCheckbox/StyledCheckbox";

type Props = {
  show: boolean,
  setShow: Dispatch<SetStateAction<boolean>>,
}

type Node = {
  id: number,
  name: string,
  parent: number,
  existing?: string,
  expanded: boolean,
  children: Node[],
}

const ImportNextProjectModal : FC<Props> = ({show, setShow}) => {
  const [t] = useTranslation('projects');
  const [projectTree, setProjectTree] = useState<Node>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {      
      (async () => {
        setLoading(true);
        const projects = await ServiceNextProject.listProjects();
        const nodes = new Map<number, Node>();
        const root = {
            id: 0,
            name: "root",
            parent: -1,
            expanded: false,
            children: [],
        };
        nodes.set(0, root);
        for (const project of projects) {
          nodes.set(project.id, {
            id: project.id,
            name: project.name,
            parent: project.parentprojectid ?? 0,
            existing: project.existing,
            expanded: false,
            children: [],
          });
        }
        for (const project of projects) {
          const node = nodes.get(project.id);
          if (node) {
            const parent = nodes.get(node.parent);
            if (parent) {
              parent.children.push(node);
            }
          }
        }
        setProjectTree(root);
        setLoading(false);
      })();
    }
  }, [show]);

  const renderTree = (node: Node, depth = 0) => {
    return <div key={node.id} className={styles.node}> 
        {node.children.length > 0 ? (
          <div style={{lineHeight: '24px', cursor: 'pointer'}} onClick={() => {
            node.expanded = !node.expanded;
            if (projectTree) {
              setProjectTree({...projectTree});
            }
          }}>
            <span className={styles.expandCollapse}>{node.expanded ? '-' : '+'}</span>
            {node.name}            
          </div>
        ) : (
          <div style={{lineHeight: '24px'}}>
            <label>
              <StyledCheckbox 
                checked={node.expanded}
                onChange={() => {
                  node.expanded = !node.expanded;
                  if (projectTree) {
                    setProjectTree({...projectTree});
                  }
                }}
                style={{ padding: 0 }}
              />{" "}
              {node.name} {node.existing && (
                <TooltipContainer renderReferenceComponent={(className, ref) =>
                  <span className={className} ref={ref} style={{ color: "var(--gray-400)" }}>{t('importNextProjectModal.existing', '(finns redan)')}</span>
                }>
                  <div className={styles.existing}>
                    {t(
                      'importNextProjectModal.existingTooltip', 
                      `Projektet är redan länkat till projektet {{existing}}.
                      
                      Om du importerar projeket igen kommer eventuell slutkostnad uppdateras och inköp vars konto matchar en kategori som inte redan finns i Accurator skapas.`, 
                      { existing: node.existing }
                    )}
                  </div>
                </TooltipContainer>
              )}
            </label>
          </div>
        )}
      {node.expanded && node.children.map(child => renderTree(child, depth + 1))}
    </div>
  }

  const getSelectedProjects = (node : Node) : Node[] => {
    if (node.children.length > 0) {
      return node.children.flatMap(child => getSelectedProjects(child));
    } else {
      if (node.expanded) {
        return [node];
      }
    }
    return [];
  }

  const selectedProjects = useMemo(() => projectTree ? getSelectedProjects(projectTree) : [], [projectTree]);

  const importProjects = useCallback(async () => {
    if (selectedProjects.length > 0) {
      const response = await ServiceNextProject.importProjects(selectedProjects.map(node => node.id));
      if (response.missingAccounts && response.missingAccounts.length > 0) {
        NotificationManager.warning(
          t(
            'importNextProjectModal.warning.missingAccounts.text', 
            'Projekten har importerats, men kategorier saknas för följande konton: {{missingAccounts_list}}.',
            { missingAccounts: response.missingAccounts },             
          ), 
          t('importNextProjectModal.warning.missingAccounts.headline', 'Importerat')
        );
      } else {
        NotificationManager.success(
          t('importNextProjectModal.success.text', 'Projekten har importerats'), 
          t('importNextProjectModal.success.headline', 'Importerat')
        );
      }
      
      setShow(false);                
    }
  }, [projectTree]);


  return <Modal
      title={t('importNextProjectModal.headline', 'Importera från Next Project')} 
      show={show}
      setShow={setShow}
      isScrollable={true}
      buttonInfo={{
        label: t('importNextProjectModal.button.import', 'Importera'), 
        action: importProjects,
        disabled: selectedProjects.length === 0,
      }}      
    >
      {loading ? (
        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
          <Loading type="inline" color="#ff5e15" size="30px" />
        </div>
      ) : (<>
        <div className={styles.tree}>
          {projectTree && projectTree.children.map(child => renderTree(child, 0))}
        </div>
      </>)}
    </Modal>
};

export default ImportNextProjectModal;
